import classNames from 'classnames/bind';
import { KeyConstants } from '../../../types/enums';
import FAIcon, { IconStyle } from '../../../Icons/Icons';
import { type PaginationProps } from '../../types/propType';
import { scrollToTop } from '../Pagination';
import { TableRowType } from '../../Table';
import { usePaginationPosition } from '../../../hooks';
import styles from './MLP1Pagination.module.scss';

const FIRST_PAGE = 0; // First page index of pagination.
const SECOND_PAGE = 1; // Second page index of pagination to show ellipsis.
const MAX_PAGE = 9; // Last page index. Only 10 pages should be visible.
const isLargerThanMaxPage = (currentPage: number) => currentPage > MAX_PAGE;
const cx = classNames.bind(styles);

const MLP1Pagination = ({
  currentPage,
  ariaLabelPrevPage,
  ariaLabelNextPage,
  updatePaginationData,
  goToPreviousPage,
  goToNextPage,
  hasNextPage,
  scrollToTopRefId,
  paginationType,
  enableStickyPagination,
  totalRecords,
  rowsPerPage,
}: PaginationProps) => {
  const renderPaginationItems = () => {
    const items: JSX.Element[] = [];
    for (let i = FIRST_PAGE; i <= (isLargerThanMaxPage(currentPage) ? MAX_PAGE : currentPage); i++) {
      if (i === SECOND_PAGE && isLargerThanMaxPage(currentPage)) {
        items.push(<FAIcon key="ellipsis" iconName={'ellipsis'} iconStyle={IconStyle.SOLID} />);
      } else {
        const currentPageCondition = i !== FIRST_PAGE ? i + currentPage - MAX_PAGE : i;
        const pageNumber = isLargerThanMaxPage(currentPage) ? currentPageCondition : i;
        items.push(
          <div
            role="button"
            aria-label={'page-numbers'}
            data-testid={`page-numbers-${i}`}
            tabIndex={0}
            key={pageNumber}
            onClick={() => {
              updatePaginationData({ currentPage: pageNumber });
              scrollToTop(scrollToTopRefId);
            }}
            onKeyDown={() => {
              updatePaginationData({ currentPage: pageNumber });
              scrollToTop(scrollToTopRefId);
            }}
            className={`${styles['page-numbers']} ${pageNumber === currentPage ? styles['active'] : ''}`}
          >
            {`${pageNumber + 1}`}
          </div>,
        );
      }
    }
    return items;
  };

  const setDisabledClass = (condition: boolean) => (condition ? styles['disabled'] : '');

  const shouldHandleKeyDown = (e?: React.KeyboardEvent<HTMLDivElement>, callback?: () => void) => {
    if (callback && e && [KeyConstants.ENTER, KeyConstants.SPACE].includes(e.code as KeyConstants)) {
      callback();
    }
  };
  usePaginationPosition(enableStickyPagination, currentPage, totalRecords, scrollToTopRefId, rowsPerPage);

  const ContainerClassName = cx('mlp1-pagination', {
    'compact-pagination': paginationType === TableRowType.COMPACT,
    'sticky-pagination': enableStickyPagination,
  });
  return (
    <div id={'pagination-container'} className={ContainerClassName}>
      <div
        role="button"
        aria-label={ariaLabelPrevPage}
        tabIndex={0}
        className={`${styles['icon-container']} ${setDisabledClass(currentPage === FIRST_PAGE)}`}
        data-testid="pagination-back-button"
        onClick={goToPreviousPage}
        onKeyDown={(e) => shouldHandleKeyDown(e, goToPreviousPage)}
      >
        <FAIcon iconName={'angle-left'} className={styles['icon']} />
      </div>
      {renderPaginationItems()}
      <div
        role="button"
        aria-label={ariaLabelNextPage}
        tabIndex={0}
        className={`${styles['icon-container']} ${setDisabledClass(!hasNextPage)}`}
        data-testid="pagination-next-button"
        onClick={goToNextPage}
        onKeyDown={(e) => shouldHandleKeyDown(e, goToNextPage)}
      >
        <FAIcon iconName={'angle-right'} className={styles['icon']} />
      </div>
    </div>
  );
};

export default MLP1Pagination;
